import type {
  ICommonModelPSet,
  ICommonModelProperty,
  IModelPropertySet,
  IPropertyPanelData,
} from '@fulcrum/cdm-components';
import { t } from '@trimble/i18n';
import { getLogger } from '@trimble/logging';
import { Vector3 } from '@technology/web3d';
import { ConnectIdentifierBuilder } from '@technology/web3d-plugin-trimbim/dist/esnext/Extras/ConnectIdentifier/ConnectIdentifier';
import {
  Entity,
  Product,
  PropertyType,
  PropertyAggregation,
  PropertySet,
  TrimbimModel,
} from '@technology/web3d-plugin-trimbim';
import type { AggregationOperator } from '@technology/web3d-plugin-trimbim';
import { Accessor } from 'solid-js';
import { extractArtificialProps } from './cdm-property-formatters';
import {
  convertCommonModelValueToPanelValue,
  convertModelValueToPanelValue,
} from './cdm-value-converters';
import { getTrbModelName } from '../trim-bim/trim-bim-model-name-helpers';
import { toModelPropertyValueType } from './property-type-to-model-property-value-type';
import {
  buildSelectionUnion,
  type ObjectsSelection,
  type SelectedByViewerMap,
} from './selection-union';

const identifierBuilder = new ConnectIdentifierBuilder();

export type { SelectedByViewerMap };

interface IEntityInfo {
  id: number;
  entity: Entity;
  product: Product;
  properties: PropertySet[];
  position: Vector3;
}

const stringPropertyType = toModelPropertyValueType(PropertyType.StringValue);
const dateTimePropertyType = toModelPropertyValueType(PropertyType.DateTime);
const sharedAggregationOperator = 1 as AggregationOperator;

// Allow-listed FRN namespaces produced by the trimbim ConnectIdentifier layer.
// Anything else is treated as an opaque persistent id and wrapped under
// `frn:entity:` — a broad regex heuristic would misclassify pids that happen
// to contain an early colon (e.g. `3d:cube`).
const KNOWN_FRN_NAMESPACES = ['entity', 'tcfile', 'folder', 'lib'] as const;

/**
 * @intent Normalizes a trimbim persistent identifier to a Connect FRN.
 *
 * - Values already prefixed with `frn:` are returned unchanged.
 * - Values prefixed with a known namespace from {@link KNOWN_FRN_NAMESPACES}
 *   (e.g. `entity:xyz`, `tcfile:xyz`) are given the `frn:` prefix only.
 * - Anything else is treated as an opaque entity persistent id and is
 *   URL-encoded under the canonical `frn:entity:` namespace so it round-trips
 *   safely through URLs and query keys.
 *
 * Exported for unit testing.
 */
export function encodeEntityFrn(persistentId: string): string {
  if (persistentId.startsWith('frn:')) return persistentId;
  if (
    KNOWN_FRN_NAMESPACES.some((namespace) =>
      persistentId.startsWith(`${namespace}:`)
    )
  ) {
    return `frn:${persistentId}`;
  }
  return `frn:entity:${encodeURIComponent(persistentId)}`;
}

/**
 * Resolves object-properties panel payload from current selection state.
 * This keeps data aggregation concerns outside the UI-facing capability/controller.
 *
 * NOTE: The property values collection logic is copied from tcweb/web/src/CDM/properties-helper.ts
 */
export class ObjectPropertiesProvider {
  constructor(
    public readonly selectedByViewer: Accessor<SelectedByViewerMap>
  ) {}

  public async getPanelData(options?: {
    signal?: AbortSignal;
  }): Promise<IPropertyPanelData> {
    options?.signal?.throwIfAborted();
    const unionSelection = await buildSelectionUnion(
      this.selectedByViewer(),
      this.getPersistentObjectIds.bind(this),
      options?.signal
    );
    if (unionSelection.length === 0) {
      return { entities: [] };
    }
    return this.getPropertiesFromSelection(unionSelection, options?.signal);
  }

  private async getPersistentObjectIds(
    model: TrimbimModel,
    entityIds: number[],
    signal?: AbortSignal
  ): Promise<string[]> {
    signal?.throwIfAborted();
    const entities = await model.getEntities(entityIds);
    signal?.throwIfAborted();
    const boundingBoxes =
      await model.getBoundingBoxesForConnectIdentifier(entityIds);
    signal?.throwIfAborted();
    const out: string[] = [];

    for (let i = 0; i < entities.length; i++) {
      signal?.throwIfAborted();
      const entity = entities[i];
      try {
        let id = identifierBuilder.tryGetPersistentIdentifier(entity);
        // eslint-disable-next-line sonarjs/different-types-comparison -- identifier builder can return undefined at runtime despite the declared type
        if (id === undefined) {
          id = identifierBuilder.tryGetNonPersistentIdentifier(
            entity,
            boundingBoxes[i]
          );
        }
        out.push(id ?? '');
      } catch (error) {
        out.push('');
        getLogger().warn(`Failed to get identifier for entity ${entity.id}`, {
          error,
        });
      }
    }

    return out;
  }

  private resolveTrbModelName(modelRuntimeId: string): string {
    return modelRuntimeId.endsWith('.trb')
      ? modelRuntimeId
      : getTrbModelName(modelRuntimeId);
  }

  private getTrimbimModel(modelRuntimeId: string): TrimbimModel | undefined {
    const name = this.resolveTrbModelName(modelRuntimeId);
    for (const viewer of this.selectedByViewer().keys()) {
      const model = viewer.getModel(name);
      if (model instanceof TrimbimModel) return model;
    }
    return undefined;
  }

  private async getPropertiesFromSelection(
    selection: ObjectsSelection,
    signal?: AbortSignal
  ): Promise<IPropertyPanelData> {
    signal?.throwIfAborted();
    const count = selection.reduce(
      (sum, val) => sum + (val.objectIds?.length ?? 0),
      0
    );

    if (count === 0) return { entities: [] };

    if (count === 1) {
      const [modelRuntimeId, persistentId, runtimeId] =
        this.getFirstSelected(selection);

      const entityInfo =
        runtimeId !== -1
          ? await this.getEntityInfo(modelRuntimeId, runtimeId, signal)
          : undefined;

      if (!entityInfo) return { entities: [] };

      const layers = await this.getLayers(modelRuntimeId, [runtimeId], signal);
      const frnId = encodeEntityFrn(persistentId);

      return {
        title:
          this.getEntityDisplayName(entityInfo) ||
          t('objectProperties.defaultTitle'),
        entities: [frnId],
        entityProps: [
          {
            entityId: frnId,
            psets: [
              ...extractArtificialProps({
                entityInfo,
                persistentId,
                modelName: this.resolveTrbModelName(modelRuntimeId),
                layerNames: layers,
                stringPropertyType,
                dateTimePropertyType,
                convertModelValueToPanelValue,
                getEnumName: (value, map) => this.getEnumName(value, map),
              }),
              ...this.convertPropertySet(entityInfo.properties),
            ],
          },
        ],
      };
    }

    const persistentIds = selection.reduce(
      (arr, modelSelection) => arr.concat(modelSelection.objectIds),
      [] as string[]
    );
    const frnIds = persistentIds.map((x) => encodeEntityFrn(x));
    const commonsAcrossModels = await this.composeMultiSelectionCommonSets(
      selection,
      signal
    );
    return {
      title: t('objectProperties.multiSelectionTitle', {
        count: frnIds.length,
      }),
      entities: frnIds,
      entityProps: undefined,
      commonModelPropSets: commonsAcrossModels,
    };
  }

  private async getEntityInfo(
    modelRuntimeId: string,
    runtimeId: number,
    signal?: AbortSignal
  ): Promise<IEntityInfo | undefined> {
    signal?.throwIfAborted();
    const model = this.getTrimbimModel(modelRuntimeId);
    if (!model) return undefined;
    const entities = await model.getEntities([runtimeId]);
    signal?.throwIfAborted();
    const entity = entities[0];
    if (!entity) return undefined;
    const products = await model.getProducts([runtimeId]);
    signal?.throwIfAborted();
    const properties = await model.getProperties(runtimeId);
    signal?.throwIfAborted();
    return {
      id: runtimeId,
      entity,
      product: products[0],
      properties,
      position: new Vector3(0, 0, 0),
    };
  }

  private async getLayers(
    modelRuntimeId: string,
    entityIds: number[],
    signal?: AbortSignal
  ): Promise<string[]> {
    signal?.throwIfAborted();
    const model = this.getTrimbimModel(modelRuntimeId);
    if (!model) return [];
    const allLayerNames = await model.getLayers();
    signal?.throwIfAborted();
    if (entityIds.length === 0) return allLayerNames;
    const layerIds = await model.findEntityLayers(entityIds);
    signal?.throwIfAborted();
    return (
      [...new Set(layerIds)]
        .map((id) => allLayerNames[id])
        // eslint-disable-next-line sonarjs/different-types-comparison -- index access can be undefined at runtime (noUncheckedIndexedAccess is off)
        .filter((name): name is string => name !== undefined)
    );
  }

  private async aggregateSharedProperties(
    modelRuntimeId: string,
    entityIds: number[],
    signal?: AbortSignal
  ): Promise<PropertyAggregation[]> {
    signal?.throwIfAborted();
    const model = this.getTrimbimModel(modelRuntimeId);
    if (!model) return [];
    const aggregations: PropertyAggregation[] = [];
    for (const propertySetName of await model.getPropertySetNames()) {
      signal?.throwIfAborted();
      for (const propDefinition of await model.getPropertyDefinitions(
        propertySetName
      )) {
        signal?.throwIfAborted();
        aggregations.push({
          propertySetName,
          propertyName: propDefinition.name,
          operator: sharedAggregationOperator,
        } as PropertyAggregation);
      }
    }
    const aggregated = await model.aggregateProperties(entityIds, aggregations);
    signal?.throwIfAborted();
    return aggregated;
  }

  private async composeMultiSelectionCommonSets(
    selectionPerModel: ObjectsSelection,
    signal?: AbortSignal
  ): Promise<ICommonModelPSet[]> {
    signal?.throwIfAborted();
    let commonsAcrossModels: ICommonModelPSet[] | undefined;

    for (const modelSelection of selectionPerModel) {
      signal?.throwIfAborted();
      let sharedProperties = await this.aggregateSharedProperties(
        modelSelection.versionId,
        modelSelection.entityIds ?? [],
        signal
      );
      sharedProperties = sharedProperties.filter(
        // eslint-disable-next-line sonarjs/different-types-comparison -- property type can be missing at runtime despite the declared type
        (prop) => !(prop.value === null && prop.type === undefined)
      );

      const commonsToModel: ICommonModelPSet[] = [];
      let commonSet: ICommonModelPSet | undefined;
      for (const sharedProperty of sharedProperties) {
        if (!commonSet || commonSet.name !== sharedProperty.propertySetName) {
          commonSet = { name: sharedProperty.propertySetName, properties: [] };
          commonsToModel.push(commonSet);
        }
        commonSet.properties.push(
          this.convertPropAggregationToCommonProp(sharedProperty)
        );
      }

      commonsAcrossModels = commonsAcrossModels
        ? this.intersectCommonModelPropSets(commonsAcrossModels, commonsToModel)
        : commonsToModel;
    }

    return commonsAcrossModels ?? [];
  }

  private convertPropAggregationToCommonProp(
    a: PropertyAggregation
  ): ICommonModelProperty {
    return {
      name: a.propertyName,
      displayProperty: {
        name: a.propertyName,
        type:
          a.type !== undefined
            ? toModelPropertyValueType(a.type)
            : stringPropertyType,
        value: convertCommonModelValueToPanelValue(a.value),
      },
      // Aggregation uses "value:null" + defined "type" as a "varies across selection" sentinel.
      // eslint-disable-next-line sonarjs/different-types-comparison -- property type can be missing at runtime despite the declared type
      isSameValueAcrossSelection: !(a.value === null && a.type !== undefined),
    };
  }

  private intersectCommonModelPropSets(
    modelSet1: ICommonModelPSet[],
    modelSet2: ICommonModelPSet[]
  ): ICommonModelPSet[] {
    return modelSet1.reduce((newCommon: ICommonModelPSet[], pset1) => {
      const pset2 = modelSet2.find((x) => x.name === pset1.name);
      if (pset2) {
        const commonProps = pset1.properties.reduce(
          (newProps: ICommonModelProperty[], prop1) => {
            const prop2 = pset2.properties.find((x) => x.name === prop1.name);
            if (prop2) {
              if (
                !prop1.isSameValueAcrossSelection ||
                !prop2.isSameValueAcrossSelection ||
                prop1.displayProperty.value !== prop2.displayProperty.value
              ) {
                prop1.isSameValueAcrossSelection = false;
              }
              newProps.push(prop1);
            }
            return newProps;
          },
          []
        );
        if (commonProps.length > 0) {
          pset1.properties = commonProps;
          newCommon.push(pset1);
        }
      }
      return newCommon;
    }, []);
  }

  private convertPropertySet(psets: PropertySet[]): IModelPropertySet[] {
    // eslint-disable-next-line sonarjs/different-types-comparison -- psets can be undefined at runtime despite the declared type
    if (psets === undefined) return [];

    return psets.map((pset) => ({
      name: pset.name ?? '',
      properties: (pset.properties ?? []).map((p) => ({
        name: p.name,
        value: convertModelValueToPanelValue(p.value),
        type: toModelPropertyValueType(p.type ?? PropertyType.StringValue),
      })),
    }));
  }

  private getEnumName(val: number, map: Array<[number, string]>): string {
    const entry = map.find((x) => x[0] === val);
    return entry ? entry[1] : `${val}`;
  }

  private getFirstSelected(
    selection: ObjectsSelection
  ): [string, string, number] {
    for (const modelSelection of selection) {
      if (modelSelection.objectIds.length > 0) {
        const runtimeId = modelSelection.entityIds
          ? modelSelection.entityIds[0]!
          : -1;
        return [
          modelSelection.versionId,
          modelSelection.objectIds[0]!,
          runtimeId,
        ];
      }
    }
    return ['', '', -1];
  }

  private getEntityDisplayName(info: IEntityInfo | undefined): string {
    return (
      info?.product?.name ??
      info?.product?.objectType ??
      info?.entity?.class ??
      ''
    );
  }
}
